<template>
  <div>
    <section
      v-if="media.length == 0"
      class="hero is-medium is-gray"
    >
      <div
        class="not-found rounded-top has-text-centered is-flex is-background-gray border-b border-gary is-align-items-center is-justify-content-center"
      >
        <p class="is-family-display">
          {{ $t("not_found_image").capitalize() }}
        </p>
      </div>
    </section>

    <!-- Four equal tiles (2 × 2), the first media included; the button
         opens the full gallery when there are more -->
    <div
      v-if="media.length"
      class="is-relative"
    >
      <div class="grid has-2-cols gap-10">
        <div
          v-for="m in gridImages"
          :key="m.id"
          class="cell is-pointer is-relative"
        >
          <img
            :src="m.thumb_url || m.url"
            loading="lazy"
            class="thumbnail-slider"
            :alt="m.file_name"
            @click="openMedia(m)"
          >
          <span
            v-if="isVideoItem(m)"
            class="video-play-badge"
          >
            <i class="mdi mdi-play-circle mdi-36px" />
          </span>
        </div>
      </div>
      <button
        v-if="isShowAllImagesBtnShown"
        type="button"
        class="button is-dark is-full-width button-cta is-absolute is-opacity btn-position"
        @click="isShowGalleryGrid = true"
      >
        {{ $t("show_all_images") }}
      </button>
    </div>
    <!-- "Voir toutes les images": full-screen grid of every media as small
         tiles; a tile opens the lightbox at that photo (or plays the video) -->
    <b-modal
      v-model="isShowGalleryGrid"
      full-screen
    >
      <div class="gallery-page">
        <div class="gallery-header">
          <p class="is-family-display has-text-weight-bold is-size-5">
            {{ $t("show_all_images") }} ({{ media.length }})
          </p>
          <button
            type="button"
            class="delete is-large"
            @click="isShowGalleryGrid = false"
          />
        </div>
        <div class="gallery-grid">
          <div
            v-for="m in media"
            :key="'g' + m.id"
            class="gallery-cell is-pointer is-relative"
            @click="openMedia(m)"
          >
            <img
              :src="m.thumb_url || m.url"
              loading="lazy"
              class="gallery-thumb"
              :alt="m.file_name"
            >
            <span
              v-if="isVideoItem(m)"
              class="video-play-badge"
            >
              <i class="mdi mdi-play-circle mdi-36px" />
            </span>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- Unified viewer: photos AND videos in one prev/next flow. After the
         gallery modal in the DOM so it paints on top of it (both Bulma
         modals share z-index) -->
    <b-modal
      v-model="isShowViewer"
      full-screen
    >
      <div
        v-if="currentViewerMedia"
        class="viewer-page"
      >
        <div class="viewer-topbar">
          <span class="viewer-counter">{{ viewerIndex + 1 }} / {{ media.length }}</span>
          <button
            type="button"
            class="delete is-large"
            @click="isShowViewer = false"
          />
        </div>
        <button
          v-if="media.length > 1"
          type="button"
          class="viewer-arrow viewer-prev"
          @click="viewerStep(-1)"
        >
          <i class="mdi mdi-chevron-left mdi-48px" />
        </button>
        <div class="viewer-stage">
          <video
            v-if="isVideoItem(currentViewerMedia)"
            :key="'vv' + currentViewerMedia.id"
            :src="currentViewerMedia.url"
            controls
            autoplay
            playsinline
            class="viewer-media"
          />
          <img
            v-else
            :key="'vi' + currentViewerMedia.id"
            :src="currentViewerMedia.url"
            :alt="currentViewerMedia.file_name"
            class="viewer-media"
          >
        </div>
        <button
          v-if="media.length > 1"
          type="button"
          class="viewer-arrow viewer-next"
          @click="viewerStep(1)"
        >
          <i class="mdi mdi-chevron-right mdi-48px" />
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    media: {
      default() {
        return [];
      },
      type: Array,
    },
  },
  data() {
    return {
      isShowGalleryGrid: false,
      isShowViewer: false,
      viewerIndex: 0,
    };
  },
  computed: {
    isShowAllImagesBtnShown() {
      return this.media && this.media.length > 4;
    },
    gridImages() {
      return this.media.slice(0, 4);
    },
    currentViewerMedia() {
      return this.media[this.viewerIndex] || null;
    },
  },
  watch: {
    isShowViewer(open) {
      if (open) {
        window.addEventListener("keydown", this.onViewerKeydown);
      } else {
        window.removeEventListener("keydown", this.onViewerKeydown);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onViewerKeydown);
  },
  methods:{
    isVideoItem(m) {
      return !!(m && m.mime_type && m.mime_type.startsWith("video/"));
    },
    // Any media opens the unified viewer at its position (photos and
    // videos share the same prev/next flow)
    openMedia(m) {
      this.viewerIndex = Math.max(0, this.media.indexOf(m));
      this.isShowViewer = true;
    },
    viewerStep(step) {
      const n = this.media.length;

      this.viewerIndex = (this.viewerIndex + step + n) % n;
    },
    onViewerKeydown(e) {
      if (e.key === "ArrowLeft") {
        this.viewerStep(-1);
      } else if (e.key === "ArrowRight") {
        this.viewerStep(1);
      }
    },
  }
};
</script>

<style scoped>
.video-play-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
.viewer-page {
  background: #fff;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.viewer-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  z-index: 2;
}
.viewer-counter {
  color: #363636;
  font-weight: 600;
}
.viewer-stage {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 70px;
}
.viewer-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
.viewer-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: #fff;
  border: 1px solid #dbdbdb;
  box-shadow: 0 2px 8px rgba(19, 22, 39, 0.12);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  color: #363636;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.viewer-arrow:hover {
  background: #f4f5f7;
}
.viewer-prev {
  left: 14px;
}
.viewer-next {
  right: 14px;
}
@media only screen and (max-width: 768px) {
  .viewer-stage {
    padding: 56px 8px;
  }
  .viewer-arrow {
    width: 42px;
    height: 42px;
  }
  .viewer-prev {
    left: 6px;
  }
  .viewer-next {
    right: 6px;
  }
}
.cell {
  height: 300px;
  border-radius: 6px;
  overflow: hidden;
}
.thumbnail-slider {
  width: 100%;
  object-fit: cover;
  height: 100%;
  display: block;
 }
 @media only screen and (max-width: 768px) {
  .cell {
    height: 160px;
  }
 }

 .grid {
  display: grid;
 }
 .has-2-cols {
  grid-template-columns: repeat(2, 1fr);
 }
 .gap-10 {
  gap:10px;
 }
 .is-pointer {
  cursor: pointer;
 }
 .is-absolute {
  position: absolute;
 }
 .is-opacity {
  opacity: 0.8;
 }
 .btn-position {
  bottom: 12px;
  right: 12px;
  left: auto;
  width: auto;
 }
 .is-relative {
  position: relative;
 }
 .not-found {
  background: #e4e4e4;
    border: solid 1px #ccc;
    border-radius: 7px;
    width: 100%;
    height: 400px;
 }
 .gallery-page {
  background: #fff;
  min-height: 100vh;
  padding: 20px 24px 40px;
  max-width: 1100px;
  margin: 0 auto;
 }
 .gallery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
 }
 .gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
 }
 .gallery-cell {
  height: 220px;
  border-radius: 6px;
  overflow: hidden;
 }
 .gallery-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
 }
 @media only screen and (max-width: 1024px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .gallery-cell {
    height: 180px;
  }
 }
 @media only screen and (max-width: 600px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .gallery-cell {
    height: 140px;
  }
 }
</style>
